import React from 'react';
import {
  Pencil, Trash, Plus, Archive, RefreshCw,
} from 'lucide-react-native';
import { lifecycleConfig } from '../DataColumnConfig';
import StateChangeSelector from '../StateChangeSelector';
import type { ActionHandlerProps, ActionConfig } from '../DataTable/actionTypes';
import log from '../../../common/utils/logger';

export const genericActionConfigs: Record<string, Partial<ActionConfig>> = {
  create: {
    id: 'create',
    label: 'Create',
    icon: Plus,
    position: 'header',
    handler: ({ utils, objectName }: ActionHandlerProps) => {
      const { navigation } = utils;
      navigation.navigate('CreateEditForm', {
        objectName,
        action: 'create', // Changed from mode to action
        data: null,
      });
    },
  },
  edit: {
    id: 'edit',
    label: 'Edit',
    icon: Pencil,
    position: 'row',
    handler: ({ data, utils, objectName }: ActionHandlerProps) => {
      const { navigation } = utils;
      navigation.navigate('CreateEditForm', {
        objectName,
        action: 'edit',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        id: (data as any)?.id,
        data,
      });
    },
  },
  changeState: {
    id: 'changeState',
    label: 'Change State',
    icon: RefreshCw,
    position: 'bulk',
    bulk: true,
    availCondition: (data, objectName) => !!lifecycleConfig[objectName as string]?.states,
    handler: ({
      data, mutations, utils, objectName,
    }: ActionHandlerProps) => {
      const { updateMutation } = mutations;
      const {
        showDialog, hideDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;

      const isBulk = Array.isArray(data);
      const items = (isBulk ? (data as unknown[]) : [data]) as Record<string, unknown>[];
      const count = items.length;
      const config = lifecycleConfig[objectName];

      if (!config?.states) return;

      showDialog({
        title: isBulk ? `Change State for ${count} Items` : 'Change State',
        message: 'Select the new target state for the selected item(s):',
        children: (
          <StateChangeSelector
            states={config.states}
            onCancel={hideDialog}
            onConfirm={async (targetStateId: string) => {
              try {
                utils.setActionRunning(true);
                const targetState = config.states?.find((s) => s.id === targetStateId);
                const promises = items.map((item) => updateMutation.mutateAsync({
                  ...item,
                  state: targetStateId,
                }));
                await Promise.all(promises);
                showToastMessage(
                  'success',
                  isBulk ? `State updated to '${targetState?.label}' for ${count} items` : `State updated to '${targetState?.label}'`,
                );
                hideDialog();

                // Final invalidation after bulk operation to ensure all side effects are captured
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });

                  // If we're updating courses, also invalidate modules as they might be affected
                  if (objectName === 'courses') {
                    queryClient.invalidateQueries({ queryKey: ['/modules'] });
                  }
                }

                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error(`Change state ${objectName} error:`, error);
                showToastMessage('error', `Failed to update state: ${(error as Error).message}`);
              } finally {
                utils.setActionRunning(false);
              }
            }}
          />
        ),
        buttons: [], // Buttons are now inside the child component
      });
    },
  },
  toggleDeprecated: {
    id: 'toggleDeprecated',
    label: 'Toggle Deprecated',
    icon: Archive,
    position: 'bulk',
    bulk: true,
    availCondition: (data, objectName) => !!lifecycleConfig[objectName as string]?.supportsDeprecation,
    handler: ({
      data, mutations, utils, objectName,
    }: ActionHandlerProps) => {
      const { updateMutation } = mutations;
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;

      const isBulk = Array.isArray(data);
      const items = (isBulk ? (data as unknown[]) : [data]) as (Record<string, unknown> & { isDeprecated?: boolean })[];
      const count = items.length;

      showDialog({
        title: isBulk ? `Toggle Deprecated for ${count} Items` : 'Toggle Deprecated',
        message: isBulk
          ? `Are you sure you want to toggle the deprecated status for these ${count} items?`
          : 'Are you sure you want to toggle the deprecated status for this item?',
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Toggle',
            action: 'primary',
            onPress: async () => {
              try {
                utils.setActionRunning(true);
                if (isBulk) {
                  const promises = items.map((item) => updateMutation.mutateAsync({
                    ...item,
                    isDeprecated: !item.isDeprecated,
                  }));
                  await Promise.all(promises);
                } else {
                  const item = data as Record<string, unknown> & { isDeprecated?: boolean };
                  await updateMutation.mutateAsync({
                    ...item,
                    isDeprecated: !item.isDeprecated,
                  });
                }
                showToastMessage(
                  'success',
                  isBulk ? `Deprecated status toggled for ${count} items` : 'Deprecated status toggled successfully',
                );

                // Final invalidation after bulk operation
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });

                  // If we're updating courses, also invalidate modules
                  if (objectName === 'courses') {
                    queryClient.invalidateQueries({ queryKey: ['/modules'] });
                  }
                }

                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error(`Toggle deprecated ${objectName} error:`, error);
                showToastMessage('error', `Failed to toggle deprecated status: ${(error as Error).message}`);
              } finally {
                utils.setActionRunning(false);
                // Clear selection after bulk operation
                if (isBulk) {
                  utils.onClearSelection?.();
                  utils.onExitSelectionMode?.();
                }
              }
            },
          },
        ],
      });
    },
  },
  delete: {
    id: 'delete',
    label: 'Delete',
    icon: Trash,
    position: 'row',
    bulk: true,
    handler: ({
      data, mutations, utils, objectName,
    }: ActionHandlerProps) => {
      const { deleteMutation } = mutations;
      const {
        showDialog, showToastMessage, setRefreshKey, queryClient,
      } = utils;

      const isBulk = Array.isArray(data);
      const items = (isBulk ? (data as unknown[]) : [data]) as (Record<string, unknown> & { id: string | number })[];
      const count = items.length;

      const label = objectName.charAt(0).toUpperCase() + objectName.slice(1);

      showDialog({
        title: isBulk ? `Delete ${count} Items` : `Delete ${label}`,
        message: isBulk
          ? `Are you sure you want to delete these ${count} items?`
          : 'Are you sure you want to delete this item?',
        buttons: [
          { text: 'Cancel', variant: 'outline', action: 'secondary' },
          {
            text: 'Delete',
            action: 'negative',
            onPress: async () => {
              try {
                utils.setActionRunning(true);
                if (isBulk) {
                  const promises = items.map((item) => deleteMutation.mutateAsync(item.id));
                  await Promise.all(promises);
                } else {
                  const item = data as Record<string, unknown> & { id: string | number };
                  await deleteMutation.mutateAsync(item.id);
                }
                showToastMessage(
                  'success',
                  isBulk ? `${count} items deleted successfully` : `${label} deleted successfully`,
                );

                // Final invalidation after bulk operation
                if (queryClient) {
                  queryClient.invalidateQueries({ queryKey: [`/${objectName}`] });

                  // If we're deleting courses, also invalidate modules
                  if (objectName === 'courses') {
                    queryClient.invalidateQueries({ queryKey: ['/modules'] });
                  }
                }

                setRefreshKey(Date.now());
              } catch (error: unknown) {
                log.error(`Delete ${objectName} error:`, error);
                showToastMessage('error', `Failed to delete ${objectName}: ${(error as Error).message}`);
              } finally {
                utils.setActionRunning(false);
                // Clear selection after bulk operation
                if (isBulk) {
                  utils.onClearSelection?.();
                  utils.onExitSelectionMode?.();
                }
              }
            },
          },
        ],
      });
    },
  },
  patch: {
    id: 'patch',
    label: 'Update Field',
    position: 'none',
    handler: async ({
      data, mutations, utils, objectName,
    }: ActionHandlerProps) => {
      const { updateMutation } = mutations;
      const { showToastMessage, setRefreshKey } = utils;
      const { item, field, value } = data as { item: Record<string, unknown>; field: string; value: unknown };

      try {
        utils.setActionRunning(true);
        await updateMutation.mutateAsync({
          ...item,
          [field]: value,
        });
        setRefreshKey(Date.now());
      } catch (error: unknown) {
        log.error(`Patch ${objectName} error:`, error);
        showToastMessage('error', `Failed to update ${field}: ${(error as Error).message}`);
      } finally {
        utils.setActionRunning(false);
      }
    },
  },
};
